import React, { useState, useEffect } from "react";
import { CSVLink } from "react-csv";
import {
  FormGroup,
  FormControlLabel,
  Checkbox,
  Card,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import VuiPagination from "components/VuiPagination";
import Icon from "@mui/material/Icon";
import { useParams } from "react-router-dom";

function PpmDataExport() {
  const { site_id } = useParams();
  const [ppmData, setPpmData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(12);
  const [dialogOpen, setDialogOpen] = useState(true);
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [siteName, setSiteName] = useState("");
  const [partsData, setPartsData] = useState({});
  const [selectAll, setSelectAll] = useState(false);

  const columnsOptions = [
    { label: "Task Name", value: "task_name" },
    { label: "Completion Status", value: "status" },
    { label: "Created At", value: "created_at" },
    { label: "Completed At", value: "completed_at" },
    { label: "Interval Type", value: "interval_type" },
    { label: "Interval Value", value: "interval_value" },
    { label: "Due Date", value: "due_date" },
    { label: "Breakdown Count", value: "breakdown_count" },
    { label: "Parts Details", value: "parts_details" },
    { label: "Comments Count", value: "comment_count" },
    { label: "Task Items Count", value: "taskitem_count" },
    { label: "Completed Items", value: "completed_items" },
  ];

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedColumns([]);
      setSelectAll(false);
    } else {
      setSelectedColumns(columnsOptions.filter(col => col.label !== "Task Name"));
      setSelectAll(true);
    }
  };

  useEffect(() => {
    const fetchSiteData = async () => {
      try {
        const response = await fetch(
          `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/site/profile?site_id=${site_id}`
        );
        const data = await response.json();
        setSiteName(data.name);
      } catch (error) {
        console.error("Error fetching site data:", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSiteData();
  }, [site_id]);

  const fetchPartsForTask = async (taskId) => {
    try {
      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/tasks/parts?task_id=${taskId}`
      );
      if (!response.ok) throw new Error("Failed to fetch parts");
      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error(`Error fetching parts for task ${taskId}:`, error);
      return [];
    }
  };

  const fetchPpmData = async () => {
    try {
      setLoading(true);

      const response = await fetch(
        `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/sites/tasks?site_id=${site_id}`
      );

      if (!response.ok) throw new Error("Failed to fetch PPM data");

      const tasks = await response.json();
      const partsPromises = tasks.map((task) => fetchPartsForTask(task.task_id));
      const allParts = await Promise.all(partsPromises);

      const partsMap = {};
      tasks.forEach((task, index) => {
        partsMap[task.task_id] = allParts[index];
      });
      setPartsData(partsMap);

      const formattedData = tasks.map((task) => {
        const partsDetails =
          partsMap[task.task_id]
            ?.map(
              (part) =>
                `${part.part_name} (${part.part_number}) - Qty: ${part.quantity}, Cost: ${
                  part.cost ?? "N/A"
                }`
            )
            .join("; ") || "N/A";

        const rowData = {
          "Task Name": task.task_name || "N/A",
          "Completion Status": task.status || "N/A",
          "Created At": task.created_at ? new Date(task.created_at).toLocaleString() : "N/A",
          "Completed At": task.completed_at ? new Date(task.completed_at).toLocaleString() : "N/A",
          "Interval Type": task.interval_type || "N/A",
          "Interval Value": task.interval_value || "N/A",
          "Due Date": task.due_date ? new Date(task.due_date).toLocaleString() : "N/A",
          "Breakdown Count": task.breakdown_count || 0,
          "Parts Details": partsDetails,
          "Comments Count": task.comment_count || 0,
          "Task Items Count": task.taskitem_count || 0,
          "Completed Items": task.completed_items || 0,
        };

        const filteredRow = { "Task Name": rowData["Task Name"] }; // Always include Task Name
        selectedColumns.forEach((col) => {
          if (col.label !== "Task Name") {
            filteredRow[col.label] = rowData[col.label];
          }
        });

        return filteredRow;
      });

      setPpmData(formattedData);
    } catch (error) {
      console.error("Error fetching PPM data:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => setDialogOpen(false);
  const handleSubmit = () => {
    fetchPpmData();
    handleDialogClose();
  };

  const displayedRows = ppmData.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.ceil(ppmData.length / rowsPerPage);
  const csvData = ppmData;

  const tableColumns = [
    { name: "Task Name", align: "center" },
    ...selectedColumns
      .filter((col) => col.label !== "Task Name")
      .map((col) => ({ name: col.label, align: "center" })),
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <Card>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
            <VuiTypography variant="lg" color="white">
              PPM Data Export - {siteName}
            </VuiTypography>
            <div>
              <VuiButton
                variant="contained"
                color="secondary"
                onClick={() => setDialogOpen(true)}
                sx={{ mr: 2 }}
              >
                Edit
              </VuiButton>
              <CSVLink
                data={csvData}
                filename={`PPM_Report_${siteName}_${new Date(
                  dateRange.startDate
                )
                  .toLocaleDateString("en-GB")
                  .replace(/\//g, "-")}_to_${new Date(dateRange.endDate)
                  .toLocaleDateString("en-GB")
                  .replace(/\//g, "-")}.csv`}
                style={{ textDecoration: "none" }}
              >
                <VuiButton variant="contained" color="primary">
                  Download
                </VuiButton>
              </CSVLink>
            </div>
          </VuiBox>

          {loading ? (
            <VuiBox display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </VuiBox>
          ) : (
            <Table columns={tableColumns} rows={displayedRows} />
          )}

          <VuiBox display="flex" justifyContent="center" p={2}>
            <VuiPagination>
              <VuiPagination
                item
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
              >
                <Icon>keyboard_arrow_left</Icon>
              </VuiPagination>
              {Array.from({ length: totalPages }, (_, i) => (
                <VuiPagination
                  key={i + 1}
                  item
                  active={page === i + 1}
                  onClick={() => setPage(i + 1)}
                >
                  {i + 1}
                </VuiPagination>
              ))}
              <VuiPagination
                item
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
              >
                <Icon>keyboard_arrow_right</Icon>
              </VuiPagination>
            </VuiPagination>
          </VuiBox>
        </Card>
      </VuiBox>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>Export PPM Data from {siteName}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} mt={1}>
            {/* Date & Time Range Inputs */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Date & Time"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateRange.startDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="End Date & Time"
                type="datetime-local"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateRange.endDate}
                onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
              />
            </Grid>

            {/* Column Selection */}
            <Grid item xs={12}>
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={selectAll} onChange={handleSelectAll} />}
                  label="Select All Columns"
                />
                <div
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    border: "1px solid #ccc",
                    padding: "10px",
                    borderRadius: "8px",
                    marginTop: "10px",
                  }}
                >
                  <Grid container spacing={1}>
                    {columnsOptions.map((column) => (
                      <Grid item xs={6} sm={4} key={column.value}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                column.label === "Task Name" || 
                                selectedColumns.some((col) => col.value === column.value)
                              }
                              onChange={() => {
                                if (column.label !== "Task Name") {
                                  setSelectedColumns((prev) => {
                                    const exists = prev.find((col) => col.value === column.value);
                                    return exists
                                      ? prev.filter((col) => col.value !== column.value)
                                      : [...prev, column];
                                  });
                                }
                              }}
                              disabled={column.label === "Task Name"}
                            />
                          }
                          label={column.label}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </div>
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={!dateRange.startDate || !dateRange.endDate}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default PpmDataExport;